import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  formatCheckDetails,
  mergeAnalysisDisplayItems,
} from "./report-analyzer-section";

const SECTION_LINKS = {
  checklist_complete: "checklist",
  checklist_images: "checklist",
  deviations_noted: "checklist",
  variables_complete: "variables",
  item_consumption: "item-consumption",
};

function resolveCorrectionItems(report) {
  const fromApi = report?.correction_items;
  if (Array.isArray(fromApi) && fromApi.length > 0) {
    return fromApi.map((item) => ({
      key: item.key,
      severity: item.severity === "warning" ? "warn" : "fail",
      message: item.message,
      details: item.details,
    }));
  }

  const analysisData = report?.analysis_data || {};
  return mergeAnalysisDisplayItems(
    analysisData.checks || [],
    analysisData.improvements || []
  ).filter((item) => item.status === "fail" || item.status === "warn");
}

export function ReportCorrectionSection({ report, className = "" }) {
  const { t } = useTranslation();

  const items = useMemo(() => resolveCorrectionItems(report), [
    report?.correction_items,
    report?.analysis_data,
  ]);

  if (!report?.finished || !report?.not_approved) {
    return null;
  }

  const processNote = (report?.process_note || "").trim();
  const hasItems = items.length > 0;

  if (!processNote && !hasItems) {
    return (
      <div
        className={`rounded-2xl border border-red-200 bg-red-50 px-5 py-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <XCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-red-600" />
          <div>
            <h3 className="text-base font-semibold text-red-900">
              {t("service.report.correction.title")}
            </h3>
            <p className="mt-1 text-sm text-red-800">
              {t("service.report.correction.fallback_body")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-red-200 bg-red-50/80 shadow-sm ${className}`}
    >
      <div className="border-b border-red-100 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-red-100 p-2">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-red-950">
              {t("service.report.correction.title")}
            </h3>
            <p className="mt-1 text-sm text-red-800">
              {t("service.report.correction.subtitle")}
            </p>
            {processNote ? (
              <p className="mt-3 whitespace-pre-wrap text-sm font-medium text-red-900">
                {processNote}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {hasItems ? (
        <div className="space-y-2 px-5 py-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-red-700/80">
            {t("service.report.correction.items_heading")}
          </h4>
          <ul className="space-y-2">
            {items.map((item) => {
              const sectionSlug = SECTION_LINKS[item.key];
              const sectionHref = sectionSlug
                ? `/dashboard/service/reports/${report.id}/${sectionSlug}`
                : null;
              const title = t(`service.report.analyzer.check.${item.key}`, {
                defaultValue: item.key,
              });
              const details = formatCheckDetails(item.key, item.details, t);
              const isError =
                item.severity === "fail" ||
                item.status === "fail" ||
                item.severity === "error";

              return (
                <li
                  key={item.key}
                  className={`rounded-xl border px-4 py-3 ${
                    isError
                      ? "border-red-200 bg-white"
                      : "border-amber-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900">
                        {title}
                      </div>
                      {item.message ? (
                        <p className="mt-1 text-sm text-slate-700">
                          {item.message}
                        </p>
                      ) : null}
                      {item.fixMessage ? (
                        <p className="mt-1 text-sm font-medium text-red-800">
                          {t("service.report.analyzer.fix_required")}:{" "}
                          {item.fixMessage}
                        </p>
                      ) : null}
                      {details ? (
                        <p className="mt-1 text-xs text-slate-500">{details}</p>
                      ) : null}
                    </div>
                    {sectionHref ? (
                      <Link
                        to={sectionHref}
                        className="shrink-0 text-xs font-semibold text-red-700 hover:text-red-900 hover:underline"
                      >
                        {t("service.report.correction.open_section")}
                      </Link>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
